import {useEffect} from 'react';

import {BannerCodes, defaultAutorefreshConfig} from './consts';
import {
  useYandexAdsContextSelector,
  YandexAdsContextCommonSelectors,
} from './context/YandexAdsContext';
import {useAdFoxDivAvailabilityTracker} from './hooks/useAdFoxDivAvailabilityTracker';
import {useAdFoxStats} from './hooks/useAdFoxStats';
import {useAnalyticsCallbacks} from './hooks/useAnalyticsCallbacks';
import {useApiAdapter} from './hooks/useApiAdapter';
import {useAutorefresh} from './hooks/useAutorefresh';
import {useBannerContainerId} from './hooks/useBannerContainerId';
import {useRequestContextScriptLoad} from './hooks/useRequestContextScriptLoad';
import {
  AdFoxConfig,
  AdPlatformType,
  AdType,
  BannerPlacementType,
} from './types';
import {combineAndExtendCallbacks} from './utils/combineAndExtendCallbacks';
import {getAdvManager} from './utils/getAdvManager';

type Props = {
  blockId: string;
  position?: number;
  placement: BannerPlacementType;
  adType: AdType;
  adFoxConfig: AdFoxConfig;
  bannerContainerClassName: string;
  onSuccess?: VoidFunction;
  onFailure?: (type: 'error' | 'stub') => void;
};

const Banner = ({
  blockId,
  position,
  placement,
  adType,
  adFoxConfig,
  bannerContainerClassName,
  onSuccess,
  onFailure,
}: Props) => {
  const adsDisabled = useYandexAdsContextSelector(
    YandexAdsContextCommonSelectors.getAdsDisabled,
  );
  const isFloorAd = placement.includes('floor_ad_banner');

  const containerId = useBannerContainerId(placement, position);
  const adPlatform: AdPlatformType = adFoxConfig?.enabled ? 'adfox' : 'yandex';

  const apiAdapter = useApiAdapter({adFoxConfig, containerId, type: 'banner'});

  const scriptState = useRequestContextScriptLoad(adPlatform);

  useAdFoxDivAvailabilityTracker({
    containerId,
    code: BannerCodes[placement],
    enabled: adPlatform === 'adfox' && !adsDisabled,
  });

  const createAnalyticsCallbacks = useAnalyticsCallbacks({
    platform: adPlatform,
    adType,
    position,
  });

  const {createAutorefreshCallbacks, getRefreshNumber} = useAutorefresh({
    refresh: apiAdapter.refresh,
    containerId,
    isFloorAd,
    enabled: scriptState === 'loaded',
    config: adFoxConfig.enabled
      ? adFoxConfig.autorefresh
      : defaultAutorefreshConfig,
  });

  const createAdFoxStatsCallbacks = useAdFoxStats({getRefreshNumber});

  useEffect(() => {
    if (scriptState !== 'loaded' || adsDisabled) {
      if (scriptState === 'failed') {
        onFailure?.('error');
      }
      return;
    }

    const adFoxCode = BannerCodes[placement];

    let rendered = false;

    const {callbacks, cleanup} = combineAndExtendCallbacks({
      adFoxCode,
      containerId,
      blockId,
      factories: [
        createAnalyticsCallbacks,
        createAutorefreshCallbacks,
        adPlatform === 'adfox' ? createAdFoxStatsCallbacks : null,
      ],
      callback: {
        onError: () => onFailure?.('error'),
        onStub: () => {
          if (!rendered) {
            // стаб может прилетать и при рефрешах, тогда по идее реклама у нас все равно уже видна
            onFailure?.('stub');
          }
        },
        onRender: () => {
          rendered = true;
          onSuccess?.();
        },
      },
    });

    window.yaContextCb?.push(() => {
      apiAdapter.init({
        callbacks,
        devicePlatform: getAdvManager().getPlatform(),
        blockId,
        adFoxCode,
      });
    });

    return () => {
      cleanup();
      apiAdapter.destroy();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scriptState]);

  if (scriptState === 'failed' || adsDisabled) {
    return null;
  }

  return <div id={containerId} className={bannerContainerClassName} />;
};

export default Banner;
